import React from 'react';
import { Icon } from '@/shared/icons'
import { Input } from '@/shared/components/ui'
import { formatDocumentInput, documentDigitsOnly } from '@/shared/utils/documentMask'

interface BasicUserInfoSectionProps {
  formData: any;
  isEditMode: boolean;
  onFieldChange: (field: string, value: any) => void;
}

export const BasicUserInfoSection: React.FC<BasicUserInfoSectionProps> = ({
  formData,
  isEditMode,
  onFieldChange,
}) => {
  return (
    <div
      className="rounded-xl border overflow-hidden"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      {/* Section Header (.card-h) */}
      <div
        className="flex items-start gap-3 px-[18px] py-[15px] border-b"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        <div
          className="w-[34px] h-[34px] rounded-[9px] flex items-center justify-center flex-shrink-0"
          style={{
            backgroundColor: 'var(--theme-primary-background)',
            color: 'var(--theme-primary)',
          }}
        >
          <Icon name="user" className="w-5 h-5" />
        </div>
        <div>
          <h2 className="text-[15px] font-bold" style={{ color: 'var(--theme-card-text)' }}>
            Informações Básicas
          </h2>
          <p className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            Dados pessoais e de contato do usuário
          </p>
        </div>
      </div>

      {/* Form Fields (.card-b) */}
      <div className="space-y-4 p-[18px]">
        {/* Email */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Email *
          </label>
          <Input
            type="email"
            value={formData.email || ''}
            onChange={(e) => onFieldChange('email', e.target.value)}
            disabled={isEditMode}
            placeholder="usuario@exemplo.com"
            data-testid="users-form-email"
          />
          {isEditMode && (
            <p className="text-xs mt-1" style={{ color: 'var(--theme-muted-text)' }}>
              O email não pode ser alterado após a criação do usuário
            </p>
          )}
        </div>

        {/* First and Last Name - Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* First Name */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Nome *
            </label>
            <Input
              type="text"
              value={formData.firstName || ''}
              onChange={(e) => onFieldChange('firstName', e.target.value)}
              placeholder="João"
              data-testid="users-form-firstname"
            />
          </div>

          {/* Last Name */}
          <div>
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Sobrenome *
            </label>
            <Input
              type="text"
              value={formData.lastName || ''}
              onChange={(e) => onFieldChange('lastName', e.target.value)}
              placeholder="Silva"
              data-testid="users-form-lastname"
            />
          </div>
        </div>

        {/* Phone */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Telefone
          </label>
          <Input
            type="tel"
            value={formData.phone || ''}
            onChange={(e) => onFieldChange('phone', e.target.value)}
            placeholder="(11) 98765-4321"
            data-testid="users-form-phone"
          />
        </div>

        {/* Código no ERP — é o que liga este usuário ao cadastro de vendedor da fonte externa */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--theme-card-text)' }}
          >
            Código
          </label>
          <Input
            type="text"
            value={formData.code || ''}
            onChange={(e) => onFieldChange('code', e.target.value)}
            placeholder="Código do vendedor no ERP"
            data-testid="users-form-code"
          />
        </div>

        {/* Documento (CPF) — o backend normaliza para dígitos; a máscara aqui é só cosmética */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: 'var(--theme-card-text)' }}
          >
            CPF
          </label>
          <Input
            type="text"
            value={formatDocumentInput(formData.document || '', 'PF')}
            onChange={(e) => onFieldChange('document', documentDigitsOnly(e.target.value))}
            placeholder="000.000.000-00"
            data-testid="users-form-document"
          />
        </div>
      </div>
    </div>
  );
};

BasicUserInfoSection.displayName = 'BasicUserInfoSection';
